import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DraftDataDto, UserProfileDto, UserRole } from "@green0meter/api-client";

import { DOMAIN_FORM_SUBMISSIONS } from "../domain";
import { ACTION_GET_FORM_DRAFT_DATA } from "../GetFormDraftData/useGetFormDraftData";

import { submissionsApi } from "@api/services";
import { roleGuard } from "@api";

export const ACTION_DELETE_DRAFT_DATA = "DELETE_DRAFT_DATA";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

const deleteDraftData = async (params: DraftDataDto): Promise<string> => {
  const response = await submissionsApi.submissionsControllerDeleteDraftData(params);

  return response.data;
};

export const useDeleteDraftData = (
  user: UserProfileDto | null,
  formId: string,
): {
  deleteDraftData: (params: DraftDataDto) => void;
  deleteDraftDataAsync: (params: DraftDataDto) => Promise<string>;
  deleteDraftDataEnabled: boolean;
  isSuccessDeleteDraftData: boolean;
  isPendingDeleteDraftData: boolean;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isSuccess, isPending } = useMutation({
    mutationKey: [DOMAIN_FORM_SUBMISSIONS, ACTION_DELETE_DRAFT_DATA],
    mutationFn: enabled ? deleteDraftData : undefined,
    onSuccess(deletedId, variables) {
      const queryKey = [DOMAIN_FORM_SUBMISSIONS, ACTION_GET_FORM_DRAFT_DATA, formId];
      const currentData = queryClient.getQueryData<DraftDataDto[] | undefined>(
        queryKey,
      );

      queryClient.setQueryData(queryKey, () => {
        if (!currentData) return [];

        // Remove the deleted item from the cache
        return currentData.filter((d) => d.id !== variables.id);
      });
    },
    retry: 2,
  });

  return {
    deleteDraftData: mutate,
    deleteDraftDataAsync: mutateAsync,
    deleteDraftDataEnabled: enabled,
    isSuccessDeleteDraftData: isSuccess,
    isPendingDeleteDraftData: isPending,
  };
};
